import * as React from "react";
import { ArrowLeft, ArrowRight, Clipboard, Plus, Trash } from "react-bootstrap-icons";
import Select from "react-select";
import { toast } from "react-toastify";

import { PublicPlanCard } from "../../../components/pricing-components";
import {
  BaseButton,
  IPackage,
  IPublicPlan,
  IPublicPlanConfigRequest,
  Loader,
  PublicPlanAction,
  Switch,
  buildPackageRegistrationUrl,
  selectTheme,
} from "../../../libs";
import { adminService } from "../../../services";
import "../../../components/pricing-components/pricing.scss";
import * as Styled from "./ManagePublicPlans.styled";

const errorMessage = (error: any, fallback: string) =>
  error?.response?.data?.error?.message || fallback;

export const ManagePublicPlans: React.FC = () => {
  const [packages, setPackages] = React.useState<IPackage[]>([]);
  const [plans, setPlans] = React.useState<IPublicPlanConfigRequest[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  const loadData = React.useCallback(async () => {
    setLoading(true);
    try {
      const [packageData, publicPlanData] = await Promise.all([
        adminService.getAllPackages(),
        adminService.getPublicPlans(),
      ]);
      setPackages(packageData);
      setPlans(
        publicPlanData.plans.map((plan) => ({
          packageId: plan.package.id,
          action: plan.action,
          highlighted: plan.highlighted,
        }))
      );
    } catch (error) {
      toast.error(errorMessage(error, "Unable to load public plans"));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const updatePlan = (index: number, update: Partial<IPublicPlanConfigRequest>) => {
    setPlans((current) =>
      current.map((plan, planIndex) => (planIndex === index ? { ...plan, ...update } : plan))
    );
  };

  const selectHighlightedPlan = (index: number) => {
    setPlans((current) =>
      current.map((plan, planIndex) => ({
        ...plan,
        highlighted: planIndex === index,
      }))
    );
  };

  const movePlan = (index: number, direction: -1 | 1) => {
    const destination = index + direction;
    if (destination < 0 || destination >= plans.length) return;
    setPlans((current) => {
      const updated = [...current];
      [updated[index], updated[destination]] = [updated[destination], updated[index]];
      return updated;
    });
  };

  const addPlan = () => {
    const usedPackageIds = new Set(plans.map((plan) => plan.packageId));
    const availablePackage = packages.find((packageItem) => !usedPackageIds.has(packageItem.id));
    if (!availablePackage) {
      toast.info("There are no additional packages available");
      return;
    }
    setPlans((current) => [
      ...current,
      {
        packageId: availablePackage.id,
        action: "PURCHASE",
        highlighted: false,
      },
    ]);
  };

  const copyPurchaseLink = async (packageId: string) => {
    try {
      await navigator.clipboard.writeText(buildPackageRegistrationUrl(packageId));
      toast.success("Purchase link copied");
    } catch (_error) {
      toast.error("Unable to copy the purchase link");
    }
  };

  const savePlans = async () => {
    if (plans.length === 0) {
      toast.error("Select at least one public plan");
      return;
    }
    setSaving(true);
    try {
      await adminService.updatePublicPlans(plans);
      toast.success("Public pricing updated");
    } catch (error) {
      toast.error(errorMessage(error, "Unable to update public pricing"));
    } finally {
      setSaving(false);
    }
  };

  const previewPlans = plans.flatMap((plan, displayOrder) => {
    const packageItem = packages.find((item) => item.id === plan.packageId);
    if (!packageItem) return [];
    const { stripeConfig: _stripeConfig, ...publicPackage } = packageItem;
    return [
      {
        package: publicPackage,
        displayOrder,
        action: plan.action,
        highlighted: plan.highlighted,
      } as IPublicPlan,
    ];
  });

  if (loading) return <Loader />;

  return (
    <Styled.Container>
      <Styled.Header>
        <div>
          <Styled.Title>Manage Public Plans</Styled.Title>
          <Styled.Description>
            Choose up to three packages for the public pricing page. Package details continue to be
            managed from Manage Packages.
          </Styled.Description>
        </div>
        <Styled.SaveButtonWrapper>
          <BaseButton onClick={savePlans} disabled={saving || plans.length === 0}>
            {saving ? "Saving..." : "Save Public Plans"}
          </BaseButton>
        </Styled.SaveButtonWrapper>
      </Styled.Header>

      <Styled.Slots>
        {previewPlans.map((previewPlan, index) => {
          const config = plans[index];
          const selectedIds = new Set(plans.map((plan) => plan.packageId));
          const packageOptions = packages.map((packageItem) => ({
            value: packageItem.id,
            label: packageItem.name,
            isDisabled: packageItem.id !== config.packageId && selectedIds.has(packageItem.id),
          }));
          const actionOptions: { value: PublicPlanAction; label: string }[] = [
            {
              value: "PURCHASE",
              label: `Start with ${previewPlan.package.name}`,
            },
            { value: "CONTACT", label: "Contact Us" },
          ];
          return (
            <Styled.Slot key={`${config.packageId}-${index}`}>
              <Styled.SlotHeader>
                <span>Position {index + 1}</span>
                <Styled.IconActions>
                  <Styled.IconButton
                    type="button"
                    onClick={() => movePlan(index, -1)}
                    disabled={index === 0}
                    aria-label="Move plan left"
                  >
                    <ArrowLeft />
                  </Styled.IconButton>
                  <Styled.IconButton
                    type="button"
                    onClick={() => movePlan(index, 1)}
                    disabled={index === plans.length - 1}
                    aria-label="Move plan right"
                  >
                    <ArrowRight />
                  </Styled.IconButton>
                  <Styled.IconButton
                    type="button"
                    onClick={() => copyPurchaseLink(config.packageId)}
                    aria-label="Copy purchase link"
                  >
                    <Clipboard />
                  </Styled.IconButton>
                  <Styled.IconButton
                    type="button"
                    onClick={() =>
                      setPlans((current) =>
                        current.filter((_plan, planIndex) => planIndex !== index)
                      )
                    }
                    aria-label="Remove public plan"
                  >
                    <Trash />
                  </Styled.IconButton>
                </Styled.IconActions>
              </Styled.SlotHeader>

              <Styled.FieldLabel htmlFor={`package-${index}`}>Package</Styled.FieldLabel>
              <Select
                inputId={`package-${index}`}
                styles={selectTheme.singleSelect()}
                options={packageOptions}
                value={packageOptions.find((option) => option.value === config.packageId)}
                onChange={(option) => {
                  if (option) updatePlan(index, { packageId: option.value });
                }}
              />

              <Styled.FieldLabel htmlFor={`action-${index}`}>Button action</Styled.FieldLabel>
              <Select
                inputId={`action-${index}`}
                styles={selectTheme.singleSelect()}
                options={actionOptions}
                value={actionOptions.find((option) => option.value === config.action)}
                onChange={(option) => {
                  if (option) updatePlan(index, { action: option.value });
                }}
              />

              <Styled.SwitchRow>
                <Styled.SwitchLabel htmlFor={`highlighted-${index}`}>
                  Show as Most Popular
                </Styled.SwitchLabel>
                <Switch
                  id={`highlighted-${index}`}
                  checked={config.highlighted}
                  onChange={(event) =>
                    event.target.checked
                      ? selectHighlightedPlan(index)
                      : updatePlan(index, { highlighted: false })
                  }
                />
              </Styled.SwitchRow>

              <Styled.Preview className="platform-packages">
                <PublicPlanCard plan={previewPlan} preview />
              </Styled.Preview>
            </Styled.Slot>
          );
        })}
      </Styled.Slots>

      {plans.length < 3 && (
        <Styled.AddButton
          type="button"
          onClick={addPlan}
          disabled={plans.length >= packages.length}
        >
          <Plus /> Add public plan
        </Styled.AddButton>
      )}
    </Styled.Container>
  );
};
